/** biome-ignore-all lint/style/useNamingConvention: External API payloads */

import type { JSX } from 'react';
import type { CardType, PaymentMethodType } from '@/app_lib/types/Billing';
import type { Plan } from '@/app_lib/types/Plan';

interface PianoResource {
  name: string;
  rid: string;
}

interface PianoCustomField {
  fieldName: string;
  value: string;
}

interface TextCustomField extends PianoCustomField {
  options: string[];
  dataType: 'TEXT';
}

interface CheckboxCustomField extends PianoCustomField {
  options: string[];
  dataType: 'BOOLEAN';
}

interface SingleSelectCustomField extends PianoCustomField {
  options: string[];
  dataType: 'SINGLE_SELECT_LIST';
}

interface RenewalInvoice {
  amount: number;
  date: string;
}

export type PianoCustomFieldData = (TextCustomField | SingleSelectCustomField | CheckboxCustomField)[];

export enum MyAccountPages {
  Home = 'home',
  Profile = 'profile',
  Billing = 'billing',
  Subscriptions = 'subscriptions',
  Newsletters = 'newsletters',
}

export const MyAccountPageTitles = {
  [MyAccountPages.Home]: 'My profile',
  [MyAccountPages.Profile]: 'My profile',
  [MyAccountPages.Billing]: 'Billing',
  [MyAccountPages.Subscriptions]: 'Subscription overview',
  [MyAccountPages.Newsletters]: 'Newsletters',
};

export type UserAddress = {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export const pianoCustomFieldMap = {
  country: 'country',
  'experience-level': 'experienceLevel',
  industry: 'industry',
  'job-function': 'jobFunction',
  salutation: 'prefix',
};

export const reversePianoCustomFieldMap = Object.fromEntries(
  Object.entries(pianoCustomFieldMap).map(([key, value]) => [value, key]),
);

export interface PianoCustomFields {
  country: string;
  'experience-level': string;
  industry: string;
  'job-function': string;
  salutation: string;
}

export interface ProfileTextFields {
  email: string;
  firstName: string;
  lastName: string;
}

export interface ProfileSelectFields {
  country: string;
  experienceLevel: string;
  industry: string;
  jobFunction: string;
  prefix: string;
}

export type SelectFieldOptions = { [key in keyof ProfileSelectFields]: { label: string; value: string }[] };

export type MainAccessData = { status: 'Active' | 'Expiring' | 'Expired'; type: string } | undefined;

export interface MiscProfileFields {
  passwordless: boolean;
  pianoUid: string;
  address?: UserAddress;
  fieldOptions: SelectFieldOptions;
  mainAccessData: MainAccessData;
}

export type MyProfileData = ProfileTextFields & ProfileSelectFields & MiscProfileFields;

export type UpdatedFields = Partial<ProfileTextFields & ProfileSelectFields>;

export interface PianoAccess {
  granted: boolean;
  resource: PianoResource;
}

export interface PianoSubscription {
  end_date: number;
  status: string;
  subscription_id: string;
  start_date: number;
  term: {
    type: string;
    resource: PianoResource;
  };
}

export interface RecurlySubscription {
  uuid: string;
  created_at: string;
  current_billing_cycle: number;
  current_period_started_at: string;
  current_period_ends_at: string;
  expires_at: string;
  state: 'expired' | 'active' | 'canceled';
  plan: Plan;
  ramp_intervals: {
    starting_on: string;
  }[];
}

export interface PianoUserData {
  custom_fields: PianoCustomFieldData;
  email: string;
  first_name: string;
  last_name: string;
}

export interface SubscriptionPageData {
  accessUntil?: string;
  status: 'Active' | 'Expiring' | 'Expired';
  type: string;
  currentPeriod?: string;
  periodEndsOn: string;
  startedOn: string;
  nextInvoice?: RenewalInvoice;
  id: string;
  inTrial: boolean;
}

export interface AccountInvoice {
  amount: number;
  date: string;
  number: string;
  period: string;
  subscription: string;
  viewUrl: string;
}

export interface RecurlyBillingInfo {
  id: string;
  first_name: string;
  last_name: string;
  address: {
    country: string;
    postal_code: string;
  };
  primary_payment_method: boolean;
  backup_payment_method: boolean;
}

export interface RecurlyCreditCardPaymentMethod extends RecurlyBillingInfo {
  payment_method: {
    object: 'credit_card';
    card_type: string;
    last_four: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface RecurlyOtherPaymentMethod extends RecurlyBillingInfo {
  payment_method: {
    object: 'braintree_apple_pay' | 'braintree_v_zero' | 'google_pay';
  };
}

export type RecurlyPaymentMethod = RecurlyCreditCardPaymentMethod | RecurlyOtherPaymentMethod;

export interface AccountCreditCardPaymentMethod {
  type: PaymentMethodType.CreditCard;
  cardType: CardType;
  country: string;
  expMonth: string;
  expYear: string;
  firstName: string;
  id: string;
  isPrimary: boolean;
  isBackup: boolean;
  lastFour: string;
  lastName: string;
  postalCode: string;
}

export interface AccountOtherPaymentMethod {
  id: string;
  isPrimary: boolean;
  isBackup: boolean;
  type: Exclude<PaymentMethodType, PaymentMethodType.CreditCard>;
}

export type AccountPaymentMethod = AccountCreditCardPaymentMethod | AccountOtherPaymentMethod;

export type LabeledAccountPaymentMethod = AccountPaymentMethod & {
  label: JSX.Element;
};

export interface RecurlyAddress {
  street1: string;
  street2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}
export interface RecurlyAccount {
  hosted_login_token: string;
  address?: RecurlyAddress;
  first_name: string;
  last_name: string;
}

export interface RecurlyInvoice {
  origin: string;
  total: number;
  number: string;
  closed_at: string;
  line_items: {
    description: string;
    start_date: string;
    end_date: string;
  }[];
}
